import React, { useCallback, useMemo, useState } from 'react'
import { omitKeys } from 'shared/utils/types/omitKeys'
import { targetComponents } from './SelectAndLink.constants'
import {
  FullSelectionOption,
  SelectAndLinkContextValue,
  SelectAndLinkSession,
  SelectAndLinkSessionConfig,
  SelectAndLinkValue
} from './SelectAndLink.types'
import SelectAndLinkCard from './SelectAndLinkCard'
import { isMinimalSelectionOption } from './utils/isMinimalSelectionOption'

export const SelectAndLinkContext =
  React.createContext<SelectAndLinkContextValue | null>(null)

type SelectAndLinkProviderProps = {
  children: React.ReactNode
}

const emptySession: SelectAndLinkSession = {
  onSelect: () => undefined,
  targetField: '',
  targetGroup: '',
  objectclassUuid: '',
  objectClassTitle: '',
  targetComponent: targetComponents.FILTER_FIELDS,
  selectedOptions: [],
  isCompleted: false
}

const DEFAULT_FILTER_FIELDS_SESSION_ID = targetComponents.FILTER_FIELDS

export function SelectAndLinkProvider({
  children
}: SelectAndLinkProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [sessions, setSessions] = useState<
    Record<string, SelectAndLinkSession>
  >({})
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)

  const onSelectForId = useCallback(
    (sessionId: string, values: SelectAndLinkValue[]) => {
      setSessions((prev): Record<string, SelectAndLinkSession> => {
        const current = prev[sessionId]
        if (!current) return prev

        const urlOptions: FullSelectionOption[] =
          current.urlSelectedOptions ?? []

        const merged: SelectAndLinkSession['selectedOptions'] = values.flatMap(
          (v): SelectAndLinkSession['selectedOptions'] => {
            if (isMinimalSelectionOption(v)) {
              const full = urlOptions.find((o) => o.uuid === v.uuid)
              return full ? [full] : []
            }
            return [v]
          }
        )

        const accumulated = [...current.selectedOptions, ...merged].filter(
          (option, index, allOptions) =>
            allOptions.findIndex(
              (candidate) => candidate.uuid === option.uuid
            ) === index
        )

        return {
          ...prev,
          [sessionId]: {
            ...current,
            isCompleted: true,
            selectedOptions: accumulated
          }
        }
      })
    },
    []
  )

  const onClearSelection = useCallback((sessionId?: string) => {
    if (sessionId) {
      setSessions((prev) => {
        return omitKeys(prev, sessionId)
      })
      setActiveSessionId((id) => (id === sessionId ? null : id))
    } else {
      setActiveSessionId((currentId) => {
        if (currentId) {
          setSessions((prev) => {
            return omitKeys(prev, currentId)
          })
        } else {
          setSessions({})
        }
        return null
      })
    }
  }, [])

  const openSelectAndLink = useCallback(
    (config: SelectAndLinkSessionConfig, sessionId?: string) => {
      const id =
        sessionId ??
        (config.targetComponent === targetComponents.FILTER_FIELDS
          ? DEFAULT_FILTER_FIELDS_SESSION_ID
          : (config.targetField ?? `session_${Date.now()}`))

      const sessionOnSelect = (values: SelectAndLinkValue[]) => {
        onSelectForId(id, values)
      }

      setSessions((prev) => {
        const previous = prev[id]
        const shouldPreserveExisting =
          !!sessionId && (previous?.selectedOptions?.length ?? 0) > 0
        const mergedSelected = shouldPreserveExisting
          ? previous.selectedOptions
          : (config.selectedOptions ?? [])

        const newSession: SelectAndLinkSession = {
          ...emptySession,
          ...config,
          objectClassTitle: config.objectClassTitle ?? '',
          urlSelectedOptions: config.selectedOptions ?? [],
          selectedOptions: mergedSelected,
          isCompleted: shouldPreserveExisting
            ? Boolean(previous?.isCompleted)
            : false,
          onSelect: sessionOnSelect
        }

        return { ...prev, [id]: newSession }
      })
      setActiveSessionId(id)
      setIsOpen(true)
    },
    [onSelectForId]
  )

  const closeSelectAndLink = useCallback(() => {
    setIsOpen(false)
  }, [])

  const getSession = useCallback(
    (sessionId: string): SelectAndLinkSession | undefined =>
      sessions[sessionId],
    [sessions]
  )

  const removeSelectedOptionByUuid = useCallback(
    (uuid: string, sessionId?: string) => {
      const id = sessionId ?? activeSessionId
      if (!id) return
      setSessions((prev) => {
        const current = prev[id]
        if (!current) return prev
        const filtered = current.selectedOptions.filter((o) => o.uuid !== uuid)
        return {
          ...prev,
          [id]: { ...current, selectedOptions: filtered }
        }
      })
    },
    [activeSessionId]
  )

  const hasPersistentSelection =
    isOpen || Object.keys(sessions).some((id) => sessions[id]?.isCompleted)

  const value: SelectAndLinkContextValue = useMemo(
    () => ({
      openSelectAndLink,
      closeSelectAndLink,
      onClearSelection,
      isOpen,
      sessions,
      activeSessionId,
      getSession,
      hasPersistentSelection,
      removeSelectedOptionByUuid
    }),
    [
      openSelectAndLink,
      closeSelectAndLink,
      onClearSelection,
      isOpen,
      sessions,
      activeSessionId,
      getSession,
      hasPersistentSelection,
      removeSelectedOptionByUuid
    ]
  )

  return (
    <SelectAndLinkContext.Provider value={value}>
      {children}
      {isOpen && activeSessionId && sessions[activeSessionId] && (
        <SelectAndLinkCard
          session={sessions[activeSessionId]}
          onClose={closeSelectAndLink}
          onSelect={sessions[activeSessionId].onSelect}
          onCancel={() => onClearSelection(activeSessionId)}
        />
      )}
    </SelectAndLinkContext.Provider>
  )
}
